import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Badge,
  Banner,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "configureStore";
import {
  fetchBusinessTripExpenses,
} from "containers/business-trip-view-container/business-trip-container.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { deleteTripExpense } from "services/business-trip.service";
import { BusinessTripExpense, ExpenseCategory } from "types/BusinessTrip";
import PrettyDate from "components/format/PrettyDate";
import ConfirmDialog from "components/confirm-dialog/confirm-dialog";
import { useConfirmDialog } from "components/confirm-dialog/use-confirm-dialog";
import useIsPhone from "features/files/use-is-phone";
import { API_URL } from "../../constants";
import { EXPENSE_CATEGORY_LABEL_KEYS } from "./constants";

interface BusinessTripExpensesProps {
  tripId: string;
  isLocked: boolean;
  canManageExpenses: boolean;
}

const BusinessTripExpenses: React.FC<BusinessTripExpensesProps> = ({
  tripId,
  isLocked,
  canManageExpenses,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const isPhone = useIsPhone();
  const { confirmState, askConfirm, handleConfirmYes, closeConfirm } =
    useConfirmDialog();

  const { expenses, expenseTotals, expensesLoading } = useSelector(
    (state: RootState) => state.businessTripContainerReducer
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (companyId && tripId) {
      dispatch(fetchBusinessTripExpenses({ companyId, tripId }));
    }
  }, [dispatch, companyId, tripId]);

  const handleAddExpense = () => {
    dispatch(setModalxComponent("BusinessTripExpenseForm"));
    dispatch(setModalxComponentProps({ tripId }));
  };

  const handleEditExpense = (expense: BusinessTripExpense) => {
    dispatch(setModalxComponent("BusinessTripExpenseForm"));
    dispatch(setModalxComponentProps({ tripId, expense }));
  };

  // An expense is deleted once. A second tap on the fading confirm button, or a new question on a
  // card that is still on the screen, would send a second request, and the server answers it
  // «not found». After a success the id stays in the set: the expense is gone for good.
  const deletingIds = useRef(new Set<string>());

  const handleDeleteExpense = async (expenseId: string) => {
    if (!companyId || deletingIds.current.has(expenseId)) return;
    deletingIds.current.add(expenseId);
    setDeleteError(null);
    try {
      await deleteTripExpense(companyId, tripId, expenseId);
      dispatch(fetchBusinessTripExpenses({ companyId, tripId }));
    } catch (e: any) {
      deletingIds.current.delete(expenseId);
      setDeleteError(e?.response?.data?.error || "error");
    }
  };

  const missingReceiptCount = expenses.filter(
    (e) => !e.receiptFileId
  ).length;
  const canEdit = !isLocked && canManageExpenses;

  const formatAmount = (val: number) =>
    val.toLocaleString("lt-LT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const categoryLabel = (exp: BusinessTripExpense) =>
    t(EXPENSE_CATEGORY_LABEL_KEYS[exp.category] || exp.category);

  const receiptBadge = (exp: BusinessTripExpense) =>
    exp.receiptFileId ? (
      <a
        href={`${API_URL}/${companyId}/files/download/${exp.receiptFileId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Badge tone="success">{t("yes")}</Badge>
      </a>
    ) : (
      <Badge tone="warning">{t("receipt_missing")}</Badge>
    );

  // A deleted expense cannot be brought back, so a tap on «Delete» only asks.
  const askDeleteExpense = (expense: BusinessTripExpense) => {
    const { id } = expense;
    if (id) askConfirm(t("confirm_delete"), () => handleDeleteExpense(id), true);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {t("expenses")}
          </Text>
          {canEdit && (
            <Button onClick={handleAddExpense} variant="primary" size="slim">
              {t("add_expense")}
            </Button>
          )}
        </InlineStack>

        {expensesLoading && (
          <InlineStack align="center">
            <Spinner size="small" />
          </InlineStack>
        )}

        {!expensesLoading && expenses.length === 0 && (
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd" tone="subdued">
              {t("no_expenses_yet")}
            </Text>
            {canEdit && (
              <Button onClick={handleAddExpense}>
                {t("add_expense")}
              </Button>
            )}
          </BlockStack>
        )}

        {!expensesLoading && expenses.length > 0 && (
          <>
            {deleteError && (
              <Banner tone="critical" onDismiss={() => setDeleteError(null)}>
                {t(deleteError)}
              </Banner>
            )}

            {missingReceiptCount > 0 && (
              <Banner tone="warning">
                {t("missing_receipts_warning", {
                  missing: missingReceiptCount,
                  total: expenses.length,
                })}
              </Banner>
            )}

            {isPhone ? (
              <div className="space-y-3">
                {expenses.map((exp, idx) => (
                  <div
                    key={exp.id || idx}
                    className="space-y-2 rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-semibold text-gray-900">
                        {categoryLabel(exp)}
                      </span>
                      <span className="whitespace-nowrap font-semibold text-gray-900">
                        {formatAmount(exp.amountEur)} EUR
                      </span>
                    </div>
                    {exp.description && (
                      <p className="break-words text-sm text-gray-600">
                        {exp.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                      <PrettyDate dateString={exp.date} />
                      {exp.currency !== "EUR" && (
                        <span>
                          {formatAmount(exp.amount)} {exp.currency}
                        </span>
                      )}
                      {receiptBadge(exp)}
                    </div>
                    {canEdit && (
                      <div className="flex gap-3 pt-1">
                        <div className="flex-1">
                          <Button
                            size="large"
                            fullWidth
                            onClick={() => handleEditExpense(exp)}
                          >
                            {t("edit")}
                          </Button>
                        </div>
                        <div className="flex-1">
                          <Button
                            size="large"
                            fullWidth
                            tone="critical"
                            onClick={() => askDeleteExpense(exp)}
                          >
                            {t("delete")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 font-semibold text-gray-500">
                      {t("expense_category")}
                    </th>
                    <th className="text-left py-2 px-2 font-semibold text-gray-500">
                      {t("description")}
                    </th>
                    <th className="text-left py-2 px-2 font-semibold text-gray-500">
                      {t("date")}
                    </th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-500">
                      {t("amount")}
                    </th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-500">
                      {t("amount_eur")}
                    </th>
                    <th className="text-center py-2 px-2 font-semibold text-gray-500">
                      {t("receipt")}
                    </th>
                    {canEdit && (
                      <th className="text-right py-2 px-2 font-semibold text-gray-500">
                        {t("actions")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((exp, idx) => (
                    <tr
                      key={exp.id || idx}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2 px-2">{categoryLabel(exp)}</td>
                      <td className="py-2 px-2 text-gray-600 max-w-[200px] truncate">
                        {exp.description || "—"}
                      </td>
                      <td className="py-2 px-2">
                        <PrettyDate dateString={exp.date} />
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        {formatAmount(exp.amount)} {exp.currency}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        {formatAmount(exp.amountEur)} EUR
                      </td>
                      <td className="py-2 px-2 text-center">
                        {receiptBadge(exp)}
                      </td>
                      {canEdit && (
                        <td className="py-2 px-2 text-right">
                          <InlineStack gap="100" align="end">
                            <Button
                              size="slim"
                              variant="plain"
                              onClick={() => handleEditExpense(exp)}
                              accessibilityLabel={t("edit")}
                            >
                              {t("edit")}
                            </Button>
                            <Button
                              size="slim"
                              variant="plain"
                              tone="critical"
                              onClick={() => askDeleteExpense(exp)}
                              accessibilityLabel={t("delete")}
                            >
                              {t("delete")}
                            </Button>
                          </InlineStack>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            {/* Totals */}
            {expenseTotals && (
              <div className="pt-3 border-t border-gray-200">
                <BlockStack gap="200">
                  {Object.entries(expenseTotals.byCategory ?? {}).map(
                    ([cat, val]) => (
                      <div
                        key={cat}
                        className="flex justify-between text-sm text-gray-500"
                      >
                        <span>
                          {t(
                            EXPENSE_CATEGORY_LABEL_KEYS[cat as ExpenseCategory] ||
                              cat
                          )}
                        </span>
                        <span>{formatAmount(val ?? 0)} EUR</span>
                      </div>
                    )
                  )}

                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
                    <span>{t("total")}</span>
                    <span>
                      {formatAmount(expenseTotals.totalEur)} EUR
                    </span>
                  </div>

                  {expenseTotals.advanceMoney > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>{t("advance_for_expenses")}</span>
                        <span>
                          {formatAmount(expenseTotals.advanceMoney)} EUR
                        </span>
                      </div>
                      <div
                        className={`flex justify-between text-sm font-semibold ${
                          expenseTotals.difference > 0
                            ? "text-green-600"
                            : expenseTotals.difference < 0
                              ? "text-red-600"
                              : "text-gray-900"
                        }`}
                      >
                        <span>{t("difference")}</span>
                        <span>
                          {expenseTotals.difference >= 0 ? "+" : ""}
                          {formatAmount(expenseTotals.difference)} EUR
                          <span className="text-xs font-normal ml-1">
                            {expenseTotals.difference > 0
                              ? `(${t("to_reimburse")})`
                              : expenseTotals.difference < 0
                                ? `(${t("employee_returns")})`
                                : `(${t("advance_fully_used")})`}
                          </span>
                        </span>
                      </div>
                    </>
                  )}
                </BlockStack>
              </div>
            )}
          </>
        )}
      </BlockStack>
      <ConfirmDialog
        open={!!confirmState}
        message={confirmState?.message || ""}
        destructive={confirmState?.destructive}
        confirmLabel={t("delete")}
        cancelLabel={t("cancel")}
        onConfirm={handleConfirmYes}
        onClose={closeConfirm}
      />
    </Card>
  );
};

export default BusinessTripExpenses;
